/**
 * Session Detail Page
 * Refactored with shared components and consistent layout
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Row from '@amzn/meridian/row';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Toggle from '@amzn/meridian/toggle';
import { useAppSelector, useAppDispatch } from '../../../store/store';
import { fetchSession, updateSessionFields, generateSummary, clearCurrentSession } from '../../../store/sessionSlice';
import { fetchTranscript, resetTranscript } from '../../../store/transcriptSlice';
import { fetchSiteCodes, fetchSiteMetadata, clearCurrentSite } from '../../../store/siteSlice';
import { useSessionUpdates } from '../../../hooks/useSessionUpdates';
import { useSessionWriteAccess } from '../../../hooks/useSessionWriteAccess';
import { useHardReloadIfPolling } from '../../../hooks/useHardReloadIfPolling';
import { withCondition } from '../../../utils/withCondition';
import { showSuccess, showError } from '../../../store/toastsSlice';
import Select, { SelectOption, SelectOptionGroup } from '@amzn/meridian/select';
import escapeRegExp from 'lodash/escapeRegExp';
import SessionHeader from './SessionHeader';
import SessionMergeBannerComponent from './SessionMergeBanner';
import { NO_MERGE_BANNER, sessionToMergeBanner } from './dataTransformations';
import TranscriptDisplay, { TranscriptDisplayProps } from '../../shared/session/TranscriptDisplay';
import { TranslationDisplayMode } from '../../shared/session/TranscriptDisplay';
import NotesSection from '../../shared/session/NotesSection';
import QuestionsToGo from '../../shared/session/QuestionsToGo';
import ExtractedFieldsDisplay from '../../shared/session/ExtractedFieldsDisplay';
import RecordingButton from '../../shared/session/RecordingButton';
import AudioFileUpload from '../../shared/session/AudioFileUpload';
import ConsentWindowComponent from '../../shared/consent/ConsentWindowComponent';
import { deriveConsentStatus, selectConsentValue } from '../../shared/consent/consentStatus';
import { useConsentGate } from '../../shared/consent/useConsentGate';
import { withConsentGate, ConsentGateHint } from '../../shared/consent/withConsentGate';
import SessionStatusBar from '../../shared/session/SessionStatusBar';
import { FontSizeProvider, useFontSize } from '../../shared/session/FontSizeContext';
import FontSizeWidget from '../../shared/session/FontSizeWidget';
import { getFieldOrder, getFieldsByRespondent, getVisibleQuestionFields, isFieldPending, mergeWithHumanOverrides, FieldValue } from '../../../utils/fieldUtils';
import { cleanupRecording } from '../../../utils/recordingCleanup';
import { fetchFieldsConfig } from '../../../store/configSlice';
import Card, { CardRadioButton } from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import { Icon } from '@amzn/meridian';
import documentTokens from "@amzn/meridian-tokens/base/icon/document"
import microphoneTokens from '@amzn/meridian-tokens/base/icon/microphone';
import { FONT_SIZE_BODY, FONT_SIZE_SMALL, LINE_HEIGHT_BODY, TEXT_COLOR_PRIMARY, TEXT_COLOR_SECONDARY } from '../../shared/cssConstants';
import { isBilingualEnabled } from '../../../utils/featureFlags';
import {
  USE_CASE_INITIAL_INFORMATION,
  USE_CASE_INITIAL_ENCOUNTER,
  USE_CASE_INITIAL_INFORMATION_AND_ENCOUNTER,
} from '../../../constants';
import RoleSelectCards from '../../shared/session/RoleSelectCards';
import { Role, roleToUseCase, useCaseToRole, resolveResumeUseCase } from '../../../utils/profileUtils';

const QUESTIONS_GATED_HINT: ConsentGateHint = {
  panelTitle: 'Question-to-Go',
  title: 'Questions appear once consent is resolved',
  description: "Accept or decline the associate's consent to see the interview questions.",
};
const FIELDS_GATED_HINT: ConsentGateHint = {
  panelTitle: 'Extraction Fields',
  title: 'Extracted fields appear once consent is resolved',
  description: "Accept or decline the associate's consent to see extracted fields.",
};

const SessionMergeBannerWithCondition = withCondition(SessionMergeBannerComponent);

const gateQuestions = withConsentGate(QuestionsToGo, QUESTIONS_GATED_HINT);
const gateFields = withConsentGate(ExtractedFieldsDisplay, FIELDS_GATED_HINT);

/** Inner component so it can call useFontSize() inside the FontSizeProvider */
const SummaryPanel: React.FC<{
  summaryContent?: string | null;
  generateSummaryStatus: string;
  onCopy: () => void;
}> = ({ summaryContent, generateSummaryStatus, onCopy }) => {
  const { fontSize } = useFontSize();

  return (
    <div
      role="tabpanel"
      id="tabpanel-summary"
      aria-labelledby="tab-summary"
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto', padding: '20px' }}
    >
      {generateSummaryStatus === 'pending' ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Loader size="large" />
          <span style={{ marginLeft: '12px' }}>Generating summary...</span>
        </div>
      ) : summaryContent ? (
        <>
          <div style={{ lineHeight: LINE_HEIGHT_BODY, whiteSpace: 'pre-wrap', fontSize, color: TEXT_COLOR_PRIMARY }}>
            {summaryContent}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
            <Button type="link" onClick={onCopy}>
              Copy
            </Button>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: TEXT_COLOR_SECONDARY }}>
          <Icon tokens={documentTokens} color="primary" />
          <p>No summary available yet.</p>
        </div>
      )}
    </div>
  );
};

/** Site selection dropdown with search and grouping */
const SiteSelect: React.FC<{
  selectedSite: string;
  onChangeSite: (value: string) => void;
  searchQuery: string;
  onSearch: (value: string) => void;
  siteCodes: string[];
  userSites: string[];
  disabled?: boolean;
}> = ({ selectedSite, onChangeSite, searchQuery, onSearch, siteCodes, userSites, disabled }) => {
  const searchRegExp = new RegExp(escapeRegExp(searchQuery), 'i');
  const userSiteSet = new Set(userSites);
  const mySites = siteCodes.filter(
    (code) => userSiteSet.has(code) && (!searchQuery || searchRegExp.test(code))
  );
  const otherSites = siteCodes.filter(
    (code) => !userSiteSet.has(code) && (!searchQuery || searchRegExp.test(code))
  );
  const hasResults = mySites.length > 0 || otherSites.length > 0;

  return (
    <div style={{ maxWidth: '300px' }}>
      <Text type="b300" tag="label">Choose Site *</Text>
      <Select
        value={selectedSite}
        onChange={onChangeSite}
        searchQuery={searchQuery}
        onSearch={onSearch}
        placeholder="Select a site"
        disabled={disabled}
      >
        {mySites.length > 0 && (
          <SelectOptionGroup label="Your Sites">
            {mySites.map((code) => (
              <SelectOption key={code} value={code} label={code} />
            ))}
          </SelectOptionGroup>
        )}
        {otherSites.length > 0 && (
          <SelectOptionGroup label="Other Sites">
            {otherSites.map((code) => (
              <SelectOption key={code} value={code} label={code} />
            ))}
          </SelectOptionGroup>
        )}
        {!hasResults && (
          <Column alignmentVertical="center" spacing="300" spacingInset="600">
            <Text alignment="center">No results</Text>
          </Column>
        )}
      </Select>
    </div>
  );
};

const SessionDetailPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentSession, fetchSessionStatus, error, updateFieldsStatus, generateSummaryStatus } = useAppSelector((state) => state.session);
  const fieldsConfig = useAppSelector((state) => state.config.fieldsConfig);
  const { isRecording, isFileStreaming } = useAppSelector((state) => state.recording);
  const { transcripts, segments } = useAppSelector((state) => state.transcript);
  const [isEditMode, setIsEditMode] = useState(false);
  const [middleView, setMiddleView] = useState<'transcript' | 'notes' | 'summary'>('transcript');
  const [isChatMode, setIsChatMode] = useState(false);
  const [translationDisplay, setTranslationDisplay] = useState<TranslationDisplayMode>('both');

  // Detect bilingual mode from session (language_code contains comma = bilingual)
  const isBilingualMode = currentSession?.language_code?.includes(',') ?? false;

  // Gates field edits, notes, summary regeneration and continuing the conversation. The backend
  // rejects these too; this just stops the UI offering an action that would 403.
  const { canEdit } = useSessionWriteAccess(currentSession);

  // Merge state: archived sources link to their merged session and merged sessions to their
  // sources. Navigating between them goes through the polling guard like the header does.
  const hardReloadIfPolling = useHardReloadIfPolling();
  const mergeBanner = currentSession ? sessionToMergeBanner(currentSession) : null;
  const MergeBanner = SessionMergeBannerWithCondition(mergeBanner !== null);
  const navigateToSession = (path: string) => {
    if (!hardReloadIfPolling(path)) navigate(path);
  };

  // Consent status derived from the session (backend consent agent value / manual override).
  // Drives the consent gate: 'acquired' hands off to the normal transcript flow (AC2).
  const consentStatus = deriveConsentStatus(selectConsentValue(currentSession));

  // Gate the panels while consent is pending.
  const isConsentPending = consentStatus === 'pending';

  // Apply the gate here (not as a prop): each resolves to the panel or the consent hint.
  const QuestionsPanel = gateQuestions(isConsentPending);
  const FieldsPanel = gateFields(isConsentPending);

  const { openConsentModal } = useConsentGate({
    enabled: true,
    sessionId: currentSession?.session_id,
    consentStatus,
    isRecording,
    isFileStreaming,
    segments,
  });

  // When consent is declined the Transcript tab
  // is hidden, so derive the active tab off 'transcript' — derived (not an effect),
  // so there is no post-paint frame showing transcript-only chrome.
  const activeMiddleView = consentStatus === 'declined' && middleView === 'transcript'
    ? 'notes'
    : middleView;

  const showChatModeToggle = useMemo(() => {
    if (isRecording || isFileStreaming) return false;
    const allCompleted = transcripts.length > 0 &&
      transcripts.every(t => t.status === 'completed' || t.status === 'postprocess_failed');
    return allCompleted && segments.some(s => !s.isPartial && s.speaker && s.speaker !== 'Unknown');
  }, [isRecording, isFileStreaming, transcripts, segments]);

  useEffect(() => {
    if (!showChatModeToggle) setIsChatMode(false);
  }, [showChatModeToggle]);

  // Category filter for left panel (QuestionsToGo) and right panel (ExtractedFields)
  // Defaults to 'live' — shows live interview questions only
  // Auto-switches to 'all' when user navigates to Notes/Summary tab
  const [categoryFilter, setCategoryFilter] = useState<'live' | 'all'>('live');

  // Profile selection for new sessions
  const [selectedProfile, setSelectedProfile] = useState<string>(USE_CASE_INITIAL_INFORMATION);

  // Role selection for new sessions
  const [selectedRole, setSelectedRole] = useState<Role>('whs_manager');

  // Resume flow: whether the "select your role" screen is showing, and the
  // role chosen there (defaults to the role the session was started with).
  const [isResuming, setIsResuming] = useState<boolean>(false);
  const [resumeRole, setResumeRole] = useState<Role>('whs_manager');

  // Site selection for new sessions
  const userSiteCode = useAppSelector((state) => state.user.siteCode);
  const userMembershipSites = useAppSelector((state) => state.user.sites);
  // Sites to surface at the top of the picker: the user's memberships, falling
  // back to their badge site when membership hasn't loaded / is empty.
  const userSites = userMembershipSites.length > 0
    ? userMembershipSites
    : (userSiteCode ? [userSiteCode] : []);
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [siteSearchQuery, setSiteSearchQuery] = useState<string>('');
  const { siteCodes, siteCodesStatus } = useAppSelector((state) => state.site);

  // Bilingual mode toggle (only shown in beta/gamma before first recording)
  const [bilingualMode, setBilingualMode] = useState(false);

  // "New session" mode — no existing session to fetch
  const isNewSession = sessionId === 'new';

  // Tracks the previous URL sessionId param (NOT Redux state).
  // This survives Header's clearCurrentSession() because it's derived from
  // the URL param which changes independently of Redux.
  // Used to prevent stale fetchSession results from the previous session
  // from triggering the redirect effect (the "bounce back" bug).
  const prevSessionIdParamRef = useRef<string | undefined>(undefined);
  const staleSessionIdRef = useRef<string | null>(null);

  // Unified polling hook for background processing updates (reanalysis, postprocessing, evidence)
  // Paused while the "Add More Conversation" resume screen is open so a background
  // refresh doesn't disrupt it.
  useSessionUpdates(isNewSession ? currentSession?.session_id : sessionId, { paused: isResuming });

  // Fetch site codes when entering new session page
  useEffect(() => {
    if (isNewSession && siteCodesStatus === 'idle') {
      dispatch(fetchSiteCodes());
    }
  }, [isNewSession, siteCodesStatus, dispatch]);

  // Pre-select user's own site once site codes are loaded
  useEffect(() => {
    if (siteCodesStatus === 'fulfilled' && userSiteCode && !selectedSite) {
      if (siteCodes.includes(userSiteCode)) {
        setSelectedSite(userSiteCode);
      }
    }
  }, [siteCodesStatus, userSiteCode, siteCodes, selectedSite]);

  // Load field config for the selected profile (new session) or session's use_case_type (existing)
  useEffect(() => {
    if (isNewSession) {
      const profileToLoad = selectedProfile || 'navigator_default';
      dispatch(fetchFieldsConfig(profileToLoad));
    } else if (!isNewSession && currentSession?.use_case_type) {
      dispatch(fetchFieldsConfig(currentSession.use_case_type));
    }
  }, [selectedProfile, currentSession?.use_case_type, isNewSession, dispatch]);

  // Load the site's captured dropdown options for the effective site (the session's
  // group_id, or the selected site for a new session) so field dropdowns can prefer
  // site-specific options over the profile defaults.
  const effectiveSiteCode = currentSession?.group_id || selectedSite;
  useEffect(() => {
    if (effectiveSiteCode) {
      dispatch(fetchSiteMetadata(effectiveSiteCode));
    } else {
      dispatch(clearCurrentSite());
    }
  }, [effectiveSiteCode, dispatch]);

  const handleProfileChange = (profileId: string) => {
    setSelectedProfile(profileId);
  };

  const handleRoleChange = (role: Role) => {
    setSelectedRole(role);
    setSelectedProfile(roleToUseCase(role));
  };

  // Resume flow: the effective profile for the chosen resume role, upgraded to
  // the combined profile when it differs from the role the session started with.
  const resumeUseCaseType = currentSession?.use_case_type
    ? resolveResumeUseCase(currentSession.use_case_type, roleToUseCase(resumeRole))
    : roleToUseCase(resumeRole);

  const handleStartResume = () => {
    setIsResuming(true);
    setResumeRole(useCaseToRole(currentSession?.use_case_type));
  };

  const handleCancelResume = () => {
    setIsResuming(false);
  };

  // Load the field config for the chosen resume profile so the form immediately
  // reflects the (possibly upgraded → combined) question set. Fired right before
  // recording actually starts; the backend persists the upgrade on the session.
  const handleResumeBeforeStart = () => {
    if (resumeUseCaseType && resumeUseCaseType !== currentSession?.use_case_type) {
      dispatch(fetchFieldsConfig(resumeUseCaseType));
    }
    setIsResuming(false);
  };

  // Initialize page: clear old state and fetch if existing session.
  // Cleanup return handles recording teardown when sessionId changes or component unmounts.
  useEffect(() => {
    if (sessionId && !isNewSession) {
      if (currentSession?.session_id === sessionId) {
        // Update ref even on early return so navigation to /new captures the right ID
        prevSessionIdParamRef.current = sessionId;
        return;
      } 
      dispatch(clearCurrentSession());
      dispatch(resetTranscript());
      dispatch(fetchSession(sessionId));
      dispatch(fetchTranscript(sessionId));
    } else if (isNewSession) {
      // Capture the PREVIOUS URL param as the stale session ID to ignore.
      // Header's clearCurrentSession() already cleared Redux, so we can't
      // rely on currentSession here — use the URL param ref instead.
      staleSessionIdRef.current =
        prevSessionIdParamRef.current && prevSessionIdParamRef.current !== 'new'
          ? prevSessionIdParamRef.current
          : null;
      dispatch(clearCurrentSession());
      dispatch(resetTranscript());
    }
    // Always update after the effect runs
    prevSessionIdParamRef.current = sessionId;

    // Cleanup recording when sessionId changes or component unmounts.
    // Covers all navigation paths: Header tabs, logo click, browser back,
    // and session-to-session transitions (same component, different param).
    return () => {
      // Skip cleanup during the /session/new → /session/{realId} redirect.
      // When sessionId="new", the closure captures isNewSession=true.
      // The redirect changes sessionId to the real ID, triggering this cleanup.
      // But since we're staying on the same session (just URL update), skip it.
      if (isNewSession) {
        return;
      }
      cleanupRecording(dispatch);
    };
  }, [sessionId, isNewSession, dispatch]);

  // Redirect from /session/new to real session URL once created.
  // Guard: ignore if currentSession was populated by stale polling/WebSocket data
  // from the previous session (the "bounce back" bug).
  useEffect(() => {
    if (
      isNewSession &&
      currentSession?.session_id &&
      currentSession.session_id !== staleSessionIdRef.current
    ) {
      navigate(`/session/${currentSession.session_id}`, { replace: true });
    }
  }, [isNewSession, currentSession?.session_id, navigate]);

  // Right panel fields — syncs with the category filter toggle
  const rightPanelFields = useMemo(() => {
    // Sync with the Live/All toggle
    if (categoryFilter === 'live') {
      return getFieldsByRespondent(fieldsConfig, 'live');
    }
    // "All" → show all fields
    return getFieldOrder(fieldsConfig);
  }, [fieldsConfig, categoryFilter]);

  const mergedExtractedFields = useMemo(() => {
    if (!currentSession) return {};
    // Display precedence: submitted (final AUSTIN form) > human overrides > extracted (LLM).
    return mergeWithHumanOverrides(
      currentSession.extracted_fields || {},
      currentSession.human_overrides_fields,
      currentSession.submitted_fields,
    );
  }, [currentSession]);

  const answeredFields = useMemo(() => {
    if (!currentSession) return new Set<string>();
    const answered = new Set<string>();
    // Check ALL fields (not just question fields) for answered status
    const allFieldNames = getFieldOrder(fieldsConfig);
    allFieldNames.forEach((fieldName) => {
      const value = mergedExtractedFields[fieldName];
      if (value && !isFieldPending(value)) {
        answered.add(fieldName);
      }
    });
    return answered;
  }, [currentSession, fieldsConfig, mergedExtractedFields]);

  const leftPanelFields = useMemo(() => {
    const respondent = categoryFilter === 'live' ? 'live' : undefined;
    return getVisibleQuestionFields(fieldsConfig, answeredFields, respondent, mergedExtractedFields);
  }, [fieldsConfig, answeredFields, categoryFilter, mergedExtractedFields]);

  const handleSave = async (fields: Record<string, FieldValue>) => {
    if (!sessionId) return;

    try {
      await dispatch(updateSessionFields({
        sessionId,
        fields,
      })).unwrap();

      dispatch(showSuccess('Fields updated successfully'));
      setIsEditMode(false);
    } catch (error: any) {
      dispatch(showError(error || 'Failed to update fields'));
      throw error;
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  // Use the effective session ID (real ID or undefined for new)
  const effectiveSessionId = isNewSession ? currentSession?.session_id : sessionId;

  if (!sessionId) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Alert type="error">
          <p>Invalid session ID</p>
          <Button onClick={() => navigate('/sessions')} type='tertiary'>Session History</Button>
        </Alert>
      </div>
    );
  }

  // Show loader only for initial fetch (no data yet), not for background refreshes
  if (!isNewSession && fetchSessionStatus === 'pending' && !currentSession) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <Loader size="large" />
      </div>
    );
  }

  // Show error only when fetch actually failed
  if (!isNewSession && fetchSessionStatus === 'rejected') {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
        <Alert type="error">
          <h3>Error Loading Session</h3>
          <p>{error || 'Session not found'}</p>
          <div style={{ marginTop: '16px' }}>
            <Row spacing="300">
              <Button onClick={() => {
                dispatch(fetchSession(sessionId));
                dispatch(fetchTranscript(sessionId));
              }}>
                Retry
              </Button>
              <Button onClick={() => navigate('/sessions')}>
                Session History
              </Button>
            </Row>
          </div>
        </Alert>
      </div>
    );
  }

  // If no session data after successful fetch, something went wrong
  if (!isNewSession && !currentSession) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
        <Alert type="error">
          <h3>Session Not Found</h3>
          <p>The requested session could not be found.</p>
          <div style={{ marginTop: '16px' }}>
            <Button onClick={() => navigate('/sessions')}>
              Session History
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  const transcriptionDisplay:TranscriptDisplayProps = {
    viewMode:isChatMode ? 'chat' : 'paragraph',
    translationDisplayMode: translationDisplay
  }
  return (
    <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Top Section: Header & Recording Controls */}
      <div style={{
        borderBottom: currentSession ? '1px solid #e0e0e0' : 'none',
        backgroundColor: currentSession ? '#EBFEFE' : 'transparent',
        padding: currentSession ? '12px 0' : '0',
        flexShrink: 0,
      }}>
        <SessionHeader session={currentSession ?? undefined} />
      </div>

      <MergeBanner {...(mergeBanner ?? NO_MERGE_BANNER)} onNavigate={navigateToSession} />

      {/* Consent Banner — universal for all profiles */}
      {currentSession && (() => {
        const consentExtracted = currentSession.extracted_fields?.interviewee_consent_obtained;
        const consentOverride = currentSession.human_overrides_fields?.interviewee_consent_obtained;
        const consentValue = consentOverride || consentExtracted;

        if (consentValue === 'Yes') return null;

        // Declined state is conveyed by the "Consent declined" chip in the header,
        // so no banner is shown here to avoid duplicating it.
        if (consentValue === 'No') return null;

        return (
          <div style={{ padding: '8px 24px', flexShrink: 0 }}>
            <Alert type="warning">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>
                  Please acquire associate's consent before proceeding.
                </span>
                <Button
                  type="tertiary"
                  size="small"
                  onClick={openConsentModal}
                  disabled={!canEdit}
                >
                  Resolve consent
                </Button>
              </div>
            </Alert>
          </div>
        );
      })()}

      {/* Main Content: 3 Columns */}
      <div style={{ flex: 1, width: '100%', display: 'flex', minHeight: 0 }}>
        <Row widths={["20%", "60%", "20%"]} spacing="none" height="100%" width="100%">
          {/* Column 1: Questions-to-Go (LEFT) — empty while consent is pending */}
          <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRight: '2px solid #e0e0e0',
            ...(isNewSession && !currentSession ? { filter: 'blur(3px)', pointerEvents: 'none' as const } : {}),
          }}>
            <div style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
            }}>
              <QuestionsPanel
                fieldNames={leftPanelFields}
                extractedFields={mergedExtractedFields}
                sessionId={currentSession?.session_id}
                categoryFilter={categoryFilter}
                onCategoryFilterChange={setCategoryFilter}
              />
            </div>
          </div>

          {/* Column 2: Middle Content */}
          <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRight: '2px solid #e0e0e0',
          }}>
            {isNewSession && !currentSession ? (
              // New session: role selection + site selection
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <div style={{
                  padding: '40px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '32px',
                }}>
                  <div>
                    <Heading level={2}>Identify Your Role</Heading>
                    <Text type="b300">
                      Your selection will ensure the session experience and safety compliance preview are tailored to your specific operational responsibility
                    </Text>
                  </div>

                  <RoleSelectCards selectedRole={selectedRole} onRoleChange={handleRoleChange} />

                  <SiteSelect
                    selectedSite={selectedSite}
                    onChangeSite={setSelectedSite}
                    searchQuery={siteSearchQuery}
                    onSearch={setSiteSearchQuery}
                    siteCodes={siteCodes}
                    userSites={userSites}
                    disabled={siteCodesStatus === 'pending'}
                  />

                  {isBilingualEnabled() && (
                    <Row alignmentVertical="center" spacing="300">
                      <Toggle
                        checked={bilingualMode}
                        onChange={setBilingualMode}
                      />
                      <span style={{ fontSize: FONT_SIZE_BODY }}>Bilingual (EN + ES)</span>
                    </Row>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center' }}>
                    <AudioFileUpload size="large" useCaseType={selectedProfile} bilingualMode={bilingualMode} siteCode={selectedSite} disabled={!selectedSite} />
                    <RecordingButton size="large" useCaseType={selectedProfile} bilingualMode={bilingualMode} siteCode={selectedSite} disabled={!selectedSite} />
                  </div>
                </div>
              </div>
            ) : (
              <FontSizeProvider>
                {/* Tab Navigation — hidden while the "Add More Conversation" / resume role screen is showing */}
                {!isResuming && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 6px 0 12px',
                  backgroundColor: '#fff',
                }}>
                  <TabGroup
                    value={`tab-${activeMiddleView}`}
                    onChange={(value) => {
                      const newView = value.replace('tab-', '') as 'transcript' | 'notes' | 'summary';
                      setMiddleView(newView);
                      if (newView === 'notes' || newView === 'summary') {
                        setCategoryFilter('all');
                      } else if (newView === 'transcript') {
                        setCategoryFilter('live');
                      }
                      if (newView === 'summary' && !currentSession?.summary_content && effectiveSessionId && canEdit) {
                        dispatch(generateSummary(effectiveSessionId));
                      }
                    }}
                    type="classic"
                  >
                    {/* AC3: transcript tab hidden in notes-and-summary-only mode (consent declined). */}
                    {consentStatus !== 'declined' && (
                      <Tab
                        value="tab-transcript"
                        aria-controls="tabpanel-transcript"
                        id="tab-transcript"
                      >
                        Transcript
                      </Tab>
                    )}
                    <Tab
                      value="tab-notes"
                      aria-controls="tabpanel-notes"
                      id="tab-notes"
                      disabled={isRecording || isFileStreaming}
                    >
                      Notes
                    </Tab>
                    <Tab
                      value="tab-summary"
                      aria-controls="tabpanel-summary"
                      id="tab-summary"
                      disabled={isRecording || isFileStreaming}
                    >
                      Summary
                    </Tab>
                  </TabGroup>

                  {/* Font Size Widget + Action Buttons based on active tab */}
                  <div style={{ display: 'flex', gap: '12px', paddingBottom: '12px', paddingRight: '12px', alignItems: 'center', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {activeMiddleView === 'transcript' && isBilingualMode && (
                      <Select
                        value={translationDisplay}
                        onChange={(value) => setTranslationDisplay(value as TranslationDisplayMode)}
                        size="small"
                        placeholder="Language"
                        width={100}
                      >
                        <SelectOption value="en" label="EN" />
                        <SelectOption value="es" label="ES" />
                        <SelectOption value="both" label="Both" />
                      </Select>
                    )}
                    <FontSizeWidget />
                    {activeMiddleView === 'transcript' && (
                      <>
                        {showChatModeToggle && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Toggle
                              checked={isChatMode}
                              onChange={setIsChatMode}
                              size="small"
                            />
                            <span style={{ fontSize: FONT_SIZE_SMALL }}>Chat Mode</span>
                          </div>
                        )}
                        {isFileStreaming ? null : !isRecording ? (
                          <Button
                            size="small"
                            type="tertiary"
                            onClick={handleStartResume}
                            disabled={!canEdit}
                          >
                            <>
                              <Icon tokens={microphoneTokens} /> {currentSession?.full_text ? 'Add More Conversation' : 'Start Session'}
                            </>
                          </Button>
                        ) : (
                          <RecordingButton
                            hasContent={Boolean(currentSession?.full_text)}
                            size={"small"}
                            siteCode={currentSession?.group_id || selectedSite}
                          />
                        )}
                        {isFileStreaming && <AudioFileUpload size={"small"} />}
                      </>
                    )}
                    {activeMiddleView === 'summary' && effectiveSessionId && (
                      <Button
                        onClick={() => dispatch(generateSummary(effectiveSessionId))}
                        size="small"
                        disabled={!canEdit || generateSummaryStatus === 'pending'}
                      >
                        {generateSummaryStatus === 'pending' ? 'Regenerating...' : 'Regenerate Summary'}
                      </Button>
                    )}
                  </div>
                </div>
                )}

                {/* Content Area */}
                {middleView === 'transcript' && isResuming ? (
                  // Resume flow: reuse the same "Identify Your Role" screen as
                  // /session/new (without site selection) before recording.
                  <div
                    role="tabpanel"
                    id="tabpanel-transcript"
                    aria-labelledby="tab-transcript"
                    style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
                  >
                    <div style={{ padding: '40px 24px', display: 'flex', flexDirection: 'column', gap: '32px' }}>
                      <div>
                        <Heading level={2}>Identify Your Role</Heading>
                        <Text type="b300">
                          Your selection will ensure the session experience and safety compliance preview are tailored to your specific operational responsibility
                        </Text>
                      </div>

                      <RoleSelectCards selectedRole={resumeRole} onRoleChange={setResumeRole} />

                      {isBilingualEnabled() && (
                        <Row alignmentVertical="center" spacing="300">
                          <Toggle checked={bilingualMode} onChange={setBilingualMode} />
                          <span style={{ fontSize: FONT_SIZE_BODY }}>Bilingual (EN + ES)</span>
                        </Row>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center' }}>
                        <Button type="secondary" size="large" onClick={handleCancelResume}>
                          Cancel
                        </Button>
                        <AudioFileUpload
                          size="large"
                          useCaseType={resumeUseCaseType}
                          bilingualMode={bilingualMode}
                          siteCode={currentSession?.group_id || selectedSite}
                          onBeforeStart={handleResumeBeforeStart}
                        />
                        <RecordingButton
                          size="large"
                          useCaseType={resumeUseCaseType}
                          bilingualMode={bilingualMode}
                          siteCode={currentSession?.group_id || selectedSite}
                          startLabel="Continue"
                          onBeforeStart={handleResumeBeforeStart}
                        />
                      </div>
                    </div>
                  </div>
                ) : activeMiddleView === 'transcript' ? (
                  <div
                    role="tabpanel"
                    id="tabpanel-transcript"
                    aria-labelledby="tab-transcript"
                    style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
                  >
                    {/* AC2: once consent is acquired, hand off to the normal live-transcript
                        flow; until then (pending/declined) the consent window gates it. */}
                    {consentStatus === 'acquired' ? (
                      <TranscriptDisplay {...transcriptionDisplay} />
                    ) : (
                      <ConsentWindowComponent
                        transcriptionDisplay={transcriptionDisplay}
                      />
                    )}
                  </div>
                ) : activeMiddleView === 'notes' ? (
                  <div
                    role="tabpanel"
                    id="tabpanel-notes"
                    aria-labelledby="tab-notes"
                    style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
                  >
                    <NotesSection sessionId={effectiveSessionId || sessionId} canEdit={canEdit} />
                  </div>
                ) : (
                  <SummaryPanel
                    summaryContent={currentSession?.summary_content}
                    generateSummaryStatus={generateSummaryStatus}
                    onCopy={() => {
                      navigator.clipboard.writeText(currentSession?.summary_content || '').then(() => {
                        dispatch(showSuccess('Summary copied to clipboard'));
                      }).catch(() => {
                        dispatch(showError('Failed to copy summary'));
                      });
                    }}
                  />
                )}

                {/* Unified status bar — visible across all tabs */}
                <SessionStatusBar activeTab={activeMiddleView} />
              </FontSizeProvider>
            )}
          </div>

          {/* Column 3: Extracted Fields (RIGHT) — empty while consent is pending */}
          <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...(isNewSession && !currentSession ? { filter: 'blur(3px)', pointerEvents: 'none' as const } : {}),
          }}>
            <div style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
            }}>
              <FieldsPanel
                fieldNames={rightPanelFields}
                extractedFields={mergedExtractedFields}
                editable={canEdit}
                isEditMode={isEditMode}
                onEdit={() => setIsEditMode(true)}
                onSave={handleSave}
                onCancel={handleCancel}
                isSaving={updateFieldsStatus === 'pending'}
              />
            </div>
          </div>
        </Row>
      </div>
    </div>
  );
};

export default SessionDetailPage;
